import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {surveyFormatIsoDate} from "../shared/surveyQuestions.util";

type EvaluationBucket = {
    key: string
    label: string
};

export const EVALUATION_CONTENT_MAX_HEIGHT = 380;

const TEXT_QUESTION_TYPES: SurveyQuestionType[] = [
    SurveyQuestionType.SHORT_TEXT,
    SurveyQuestionType.LONG_TEXT,
];

export const isTextQuestion = (question: SurveyQuestion) => (
    !!question.type && TEXT_QUESTION_TYPES.includes(question.type)
);

export const isDateQuestion = (question: SurveyQuestion) => question.type === SurveyQuestionType.DATE;

/** Counted and nothing more: the API says how many guests uploaded, never what, so there are no buckets. */
export const isUploadQuestion = (question: SurveyQuestion) => question.type === SurveyQuestionType.UPLOAD_MEDIA;

const numericRange = (from: number, to: number) => (
    Array.from({length: to - from + 1}, (_, index) => String(from + index))
);

const sortedPresentKeys = (keys: string[], numeric: boolean) => {
    const unique = Array.from(new Set(keys));
    return numeric
        ? unique.sort((left, right) => Number(left) - Number(right))
        : unique.sort((left, right) => left.localeCompare(right));
};

export const evaluationBuckets = (
    question: SurveyQuestion,
    ctx: SurveyAnswerContext,
    valueCounts: Record<string, number> | undefined,
    compareValueCounts?: Record<string, number> | null
): EvaluationBucket[] => {
    switch (question.type) {
        case SurveyQuestionType.GENERIC_SCORE:
            return numericRange(1, 10).map(key => ({key, label: key}));

        case SurveyQuestionType.GENERIC_STARS:
            return numericRange(1, 5).map(key => ({key, label: key}));

        case SurveyQuestionType.YES_NO:
        case SurveyQuestionType.LEGAL:
            return [
                {key: 'true', label: ctx.localize(question.yesLabel)?.trim() || ctx.t('Yes')},
                {key: 'false', label: ctx.localize(question.noLabel)?.trim() || ctx.t('No')},
            ];

        case SurveyQuestionType.MULTIPLE_CHOICE:
        case SurveyQuestionType.PICTURE_CHOICE:
        case SurveyQuestionType.DROPDOWN:
        case SurveyQuestionType.CHECKLIST:
            return (question.options ?? [])
                .filter(option => !!option.id)
                .map(option => ({
                    key: option.id!,
                    label: ctx.localize(option.label)?.trim() || option.id!,
                }));

        case SurveyQuestionType.NUMBER: {
            const keys = sortedPresentKeys(
                [...Object.keys(valueCounts ?? {}), ...Object.keys(compareValueCounts ?? {})],
                true
            );
            return keys.map(key => ({key, label: key}));
        }

        case SurveyQuestionType.DATE: {
            const keys = sortedPresentKeys(
                [...Object.keys(valueCounts ?? {}), ...Object.keys(compareValueCounts ?? {})],
                false
            );
            return keys.map(key => ({key, label: surveyFormatIsoDate(key) || key}));
        }

        default:
            return [];
    }
};

export type EvaluationLabelWidth = number | Record<string, number>;

/**
 * One label column for the whole tab, so every card's bars start at the same x. Scores and stars
 * label themselves in two characters; as soon as one question answers in words, they all get the
 * wider column rather than each card picking its own.
 */
export const evaluationLabelWidth = (labels: string[]): EvaluationLabelWidth => (
    labels.every(label => label.length <= 3) ? 24 : {xs: 120, sm: 180}
);
